import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { Card, CardBody, CardHeader, Col, Row, Container } from "reactstrap";

//redux
import { useSelector, useDispatch } from "react-redux";
import TableContainer from "../../../Components/Common/TableContainer";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

import { createSelector } from "reselect";
import ImportUNGMList from "./ImportUNGMList";
import { getSortObj } from "common/lib/tableDataOptions";
import { createTableSearchParams, parseTableFilters, parseTableSearch, parseTableSort } from "common/lib/tableQueryParams";
import { getUNGMList as onGetUNGMList } from "../../../slices/thunks";
import * as moment from "moment";
import CountryFilter from "./CountryFilter";
import {
  FilterLabel,
  FilterSearchBox,
} from "Components/Common/TableFilters";

interface FilterValue {
  country: string;
}

const UNGMSanctionsList = () => {
  const location = useLocation();
  usePageActivity(location.pathname, "UNGM Debarred List");

  const [searchParams, setSearchParams] = useSearchParams();

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1, // Starts from 1
    pageSize: Number(searchParams.get("pageSize")) || 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() =>
    parseTableSort(searchParams, [{ id: "createdAt", desc: true }])
  );

  const dispatch: any = useDispatch();

  useEffect(() => {}, [dispatch]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const showAddModalForm = () => {
    setIsAddModalOpen(true);
  };

  const selectProperties = createSelector(
    (state: any) => state.SanctionsList,
    (state) => ({
      ungmList: state.ungmList.data,
      meta: state.ungmList.meta,
      error: state.error,
      loading: state.loading,
    })
  );
  const { ungmList, meta, loading } = useSelector(selectProperties);

  const fetchUNGMList = () => {
    onPageChange({
      page: 1,
      sorted: [{ id: "createdAt", desc: true }],
      searchValue: "",
    });
  };

  const [searchValue, setSearchValue] = useState(parseTableSearch(searchParams));

  const defaultFilterValue: FilterValue = {
    country: "",
  };
  const [filterValue, setFilterValue] = useState(() =>
    parseTableFilters(searchParams, defaultFilterValue)
  );

  const onPageChange = useCallback(
    ({
      page,
      sorted,
      searchValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
    }) => {
      const params = createTableSearchParams({
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
      });
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);

      let andConditions: any = [];
      if (filterValue.country) {
        andConditions.push({
          country: {
            $eq: filterValue.country,
          },
        });
      }
      andConditions.push({
        $or: [
          {
            companyName: {
              $containsi: searchValue,
            },
          },
          {
            country: {
              $containsi: searchValue,
            },
          },
          {
            grounds: {
              $containsi: searchValue,
            },
          },
          {
            contactName: {
              $containsi: searchValue,
            },
          },
          {
            contactEmail: {
              $containsi: searchValue,
            },
          },
        ],
      });

      dispatch(
        onGetUNGMList({
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [],
          filters: {
            $and: [
              {
                dataSource: {
                  $eq: "UNList",
                },
              },

              ...andConditions,
            ],
          },
        })
      );
    },
    [
      dispatch,
      filterValue,
      pagination.pageSize,
      setSearchParams,
    ]
  );

  const columns = useMemo(
    () => [
      {
        header: "Contractor Name",
        accessorKey: "companyName",
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div style={{ whiteSpace: "pre-wrap", width: "300px" }}>
              <Link
                to={`/sanctions-list/ungm/${cell.row.original?.id}`}
                className="fw-medium link-primary"
              >
                {cell.getValue()}
              </Link>
            </div>
          );
        },
      },
      {
        header: "Country",
        accessorKey: "country",
        enableColumnFilter: false,
      },
      {
        header: "Sanction Start Date",
        accessorKey: "sanctionStartDate",
        id: "sanctionStartDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date =
            (cell.getValue() &&
              moment(new Date(cell?.getValue())).format("DD MMM Y")) ||
            "";
          return <>{date}</>;
        },
      },
      {
        header: "Sanction End Date",
        accessorKey: "sanctionEndDate",
        id: "sanctionEndDate",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date =
            (cell.getValue() &&
              moment(new Date(cell?.getValue())).format("DD MMM Y")) ||
            "";
          return <>{date}</>;
        },
      },
      {
        header: "Last Updated",
        accessorKey: "updatedAt",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
          return <>{date}</>;
        },
      },
    ],
    []
  );

  document.title = "UNGM Debarred List | CIMS";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="UNGM Debarred List"
            pageTitles={[
              { title: "Dashboard", link: "/dashboard" },
              {
                title: "Sanctions List",
                link: "/sanctions-list/ungm",
              },
            ]}
          />

          <Card>
            <CardBody>
              <Row className="g-3 align-items-end">
                <Col xxl={8} lg={6}>
                  <FilterSearchBox
                    id="ungm-search"
                    placeholder="Search UNGM sanctions list..."
                    value={searchValue}
                    onChange={(value: string) => {
                      setSearchValue(value);
                      setPage(1);
                    }}
                  />
                </Col>

                <Col xxl={4} lg={6}>
                  <FilterLabel htmlFor="ungmCountryFilter">Country</FilterLabel>
                  <CountryFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, country: value });
                      setPage(1);
                    }}
                    value={filterValue.country}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={12}>
                  <h5 className="card-title">UNGM Debarred List</h5>
                  <p className="mb-0 text-muted">
                    This is a resource list of sanctioned/suspended vendors
                    maintained by UN organizations and shared through the
                    <a
                      href="https://www.ungm.org/"
                      className="mx-1 text-primary"
                    >
                      UN Global Market Place Website.
                    </a>
                  </p>
                </Col>
                {/* <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Search UNGM debarred list..."
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          setPage(1);
                        }}
                        value={searchValue}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                    <Permission resource="ungm-list" action="uploadContent">
                      <button
                        className="btn btn-primary"
                        onClick={() => showAddModalForm()}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Import
                      </button>
                    </Permission>
                  </div>
                </div> */}
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={ungmList || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap mb-0 table-striped"
                theadClass="table-light table-nowrap"
              />

              <ImportUNGMList
                isModalOpen={isAddModalOpen}
                toggle={() => setIsAddModalOpen(!isAddModalOpen)}
                fetchUNGMList={fetchUNGMList}
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UNGMSanctionsList;
